// React Flow node types: the item views plus engine chrome (connection dots,
// resize controls). Nothing here decides semantics; it reports to the shell.

import { type ItemKind, type ItemOf } from '@shared/board'
import { type Node, type NodeProps, NodeResizer, type ResizeParams } from '@xyflow/react'
import { createContext, use } from 'react'
import { CardView } from '../../items/CardView'
import { ClipView } from '../../items/ClipView'
import { MaterialView } from '../../items/MaterialView'
import { FileView } from '../../items/FileView'
import { GroupView } from '../../items/GroupView'
import { ImageView } from '../../items/ImageView'
import { LinkView } from '../../items/LinkView'
import { MessageView } from '../../items/MessageView'
import { SessionNode } from './SessionNode'
import { type EngineProps } from '../types'
import { Handles } from './Handles'

export type NodeCallbacks = Pick<EngineProps, 'onResizeEnd' | 'canResize' | 'onResetHeight'> & {
  /** Resize controls show only when exactly one item is selected. */
  soleSelected: string | null
}
export const NodeCallbacksContext = createContext<NodeCallbacks | null>(null)

function useCallbacks(): NodeCallbacks {
  const v = use(NodeCallbacksContext)
  if (!v) throw new Error('NodeCallbacksContext missing')
  return v
}

type ItemNode<K extends ItemKind> = Node<{ item: ItemOf<K> }, K>

const resizer = {
  lineClassName: 'moot-resize-line',
  handleClassName: 'moot-resize-handle',
} as const

const toRect = (p: ResizeParams) => ({
  x: Math.round(p.x),
  y: Math.round(p.y),
  w: Math.round(p.width),
  h: Math.round(p.height),
})

function CardNode({ data: { item }, selected }: NodeProps<ItemNode<'card'>>) {
  const cb = useCallbacks()
  return (
    <div
      className="relative"
      onDoubleClick={(e) => {
        // Double-clicking the top or bottom resize band returns to auto height.
        const el = e.target as Element
        if (el.closest('.moot-resize-line.top, .moot-resize-line.bottom')) {
          e.stopPropagation()
          cb.onResetHeight(item.id)
        }
      }}
    >
      <CardView item={item} selected={selected} />
      <Handles />
      <NodeResizer
        {...resizer}
        isVisible={selected && cb.soleSelected === item.id}
        minWidth={220}
        maxWidth={960}
        minHeight={80}
        onResizeEnd={(_, p) => {
          cb.onResizeEnd(item.id, toRect(p))
        }}
      />
    </div>
  )
}

function ImageNode({ data: { item }, selected }: NodeProps<ItemNode<'image'>>) {
  const cb = useCallbacks()
  return (
    <div className="relative">
      <ImageView item={item} selected={selected} />
      <Handles />
      <NodeResizer
        {...resizer}
        isVisible={selected && cb.soleSelected === item.id}
        keepAspectRatio
        minWidth={80}
        minHeight={40}
        onResizeEnd={(_, p) => {
          cb.onResizeEnd(item.id, toRect(p))
        }}
      />
    </div>
  )
}

function GroupNode({ data: { item }, selected }: NodeProps<ItemNode<'group'>>) {
  const cb = useCallbacks()
  return (
    <>
      <GroupView item={item} selected={selected} />
      <NodeResizer
        {...resizer}
        isVisible={selected && cb.soleSelected === item.id}
        minWidth={160}
        minHeight={100}
        shouldResize={(_, p) => cb.canResize(item.id, toRect(p))}
        onResizeEnd={(_, p) => {
          cb.onResizeEnd(item.id, toRect(p))
        }}
      />
    </>
  )
}

function MessageNode({ data: { item }, selected }: NodeProps<ItemNode<'message'>>) {
  return (
    <div className="relative">
      <MessageView item={item} selected={selected} />
      <Handles />
    </div>
  )
}

function FileNode({ data: { item }, selected }: NodeProps<ItemNode<'file'>>) {
  return (
    <div className="relative">
      <FileView item={item} selected={selected} />
      <Handles />
    </div>
  )
}

function LinkNode({ data: { item }, selected }: NodeProps<ItemNode<'link'>>) {
  return (
    <div className="relative">
      <LinkView item={item} selected={selected} />
      <Handles />
    </div>
  )
}

function ClipNode({ data: { item }, selected }: NodeProps<ItemNode<'clip'>>) {
  const cb = useCallbacks()
  return (
    <div className="relative">
      <ClipView item={item} selected={selected} />
      <Handles />
      <NodeResizer
        {...resizer}
        isVisible={selected && cb.soleSelected === item.id}
        keepAspectRatio
        minWidth={120}
        minHeight={60}
        onResizeEnd={(_, p) => {
          cb.onResizeEnd(item.id, toRect(p))
        }}
      />
    </div>
  )
}

function MaterialNode({ data: { item }, selected }: NodeProps<ItemNode<'material'>>) {
  return (
    <div className="relative">
      <MaterialView item={item} selected={selected} />
      <Handles />
    </div>
  )
}

export const nodeTypes = {
  session: SessionNode,
  card: CardNode,
  image: ImageNode,
  group: GroupNode,
  message: MessageNode,
  file: FileNode,
  link: LinkNode,
  clip: ClipNode,
  material: MaterialNode,
}
